import { useMemo, useState } from 'react';
import { Button, ButtonGroup } from '@cbhq/cds-web/buttons';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { SpotRectangle } from '@cbhq/cds-web/illustrations';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { TabNavigation } from '@cbhq/cds-web/tabs/TabNavigation';
import { TextDisplay2, TextTitle3, TextTitle4 } from '@cbhq/cds-web/typography';

import { ReactComponent as CommercePreviewImg } from ':cloud/assets/Products/CommercePreview.svg';
import { ReactComponent as OnrampPreviewImg } from ':cloud/assets/Products/OnrampPreview.svg';
import { ReactComponent as WalletAPIQuickstartImg } from ':cloud/assets/Products/WalletAPIQuickstart.svg';
import { useProject } from ':cloud/contexts/ProjectContext';
import { EventName, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { CodeBlockLanguage } from ':cloud/types/ts_types';
import {
  COINBASE_CDP_SDK_DISCORD_LINK,
  COMMERCE_DOCS_LINK,
  ONRAMP_DOCS_LINK,
  WALLET_API_DOCS_LINK,
  WALLET_API_TRANSFERS_DOCS_LINK,
} from ':cloud/utils/links';
import { onExternalLinkPress } from ':cloud/utils/onExternalLinkPress';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';
import { Layout } from ':cloud/widgets/layout';
import { FULL_PAGE_MAX_WIDTH, MAIN_CONTAINER_MIN_WIDTH } from ':cloud/widgets/product/constants';
import { QuickstartItem } from ':cloud/widgets/product/QuickstartItem';
import { CodeBlock } from ':cloud/widgets/sharedcomponents';
import { ExampleSection, ExampleSectionItem } from ':cloud/widgets/sharedcomponents/ExampleSection';

// eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
const imageStyle = { maxWidth: '100%', height: 'auto', width: '100%' };

const learnItems: ExampleSectionItem[] = [
  {
    title: 'Wallet API',
    description: 'A server-side SDK that provides simple APIs to create and use crypto wallets.',
    cta: 'Read more',
    onPress: onExternalLinkPress(WALLET_API_DOCS_LINK),
    image: <WalletAPIQuickstartImg style={imageStyle} />,
    tags: ['Wallets', 'Payments'],
  },
  {
    title: 'Onramp & Offramp',
    description: 'Easily bring money onchain.',
    cta: 'Read more',
    onPress: onExternalLinkPress(ONRAMP_DOCS_LINK),
    image: <OnrampPreviewImg style={imageStyle} />,
    tags: ['Payments', 'Fintech'],
  },
  {
    title: 'Commerce',
    description: 'Accept crypto payments.',
    cta: 'Read more',
    onPress: onExternalLinkPress(COMMERCE_DOCS_LINK),
    image: <CommercePreviewImg style={imageStyle} />,
    tags: ['Payments', 'Fintech'],
  },
];

const step1Codes = {
  typescript: 'npm install @coinbase/coinbase-sdk',
  python: 'pip install cdp-sdk',
};

const step2Codes = {
  typescript: 'let wallet = await Wallet.create();',
  python: 'wallet = Wallet.create()',
};

const step3Codes = {
  typescript: `// Create a faucet request that returns a Faucet transaction, which can be used to retrieve the transaction hash.
let faucetTransaction = await wallet.faucet("usdc");`,

  python: `# Create a faucet request that returns a Faucet transaction, which can be used to retrieve the transaction hash.
faucet_transaction = wallet.faucet("usdc")

# Wait for the faucet transaction to land on-chain.
faucet_transaction.wait()

faucet_transaction.transaction_hash`,
};

const step4Codes = {
  typescript: `// Transfer 0.00001 Ether to the destination address.
let transfer = await wallet.createTransfer({
  amount: 0.00001,
  assetId: Coinbase.assets.Usdc,
  destination: anotherWallet
});`,

  python: `# Transfer 0.00001 Ether to the destination address.
transfer = wallet.transfer(0.00001, "eth", another_wallet)

# Wait for the transfer to settle.
transfer.wait()`,
};

const codeBackgroundColor = 'var(--foreground)';
const codeStyle = {
  padding: '25px 10px 25px',
  background: codeBackgroundColor,
  overflow: 'clip',
};

export function SendReceive() {
  const [selectedTab, setTab] = useState('typescript');
  const title = 'Make payments onchain with one line of code';
  const description =
    'Send stablecoins and other crypto assets anywhere in the world with our intuitive APIs.';

  const step1Code = useMemo(() => {
    return [
      {
        language: selectedTab as CodeBlockLanguage,
        code: step1Codes[selectedTab],
      },
    ];
  }, [selectedTab]);

  const step2Code = useMemo(() => {
    return [
      {
        language: selectedTab as CodeBlockLanguage,
        code: step2Codes[selectedTab],
      },
    ];
  }, [selectedTab]);

  const step3Code = useMemo(() => {
    return [
      {
        language: selectedTab as CodeBlockLanguage,
        code: step3Codes[selectedTab],
      },
    ];
  }, [selectedTab]);

  const step4Code = useMemo(() => {
    return [
      {
        language: selectedTab as CodeBlockLanguage,
        code: step4Codes[selectedTab],
      },
    ];
  }, [selectedTab]);

  const { isDesktop } = useBreakpoints();

  const { selectedProject } = useProject();
  useLogViewOnMount(EventName.platform_api_page_view);

  const tabs = useMemo(
    () => [
      { id: 'typescript', label: 'TypeScript' },
      { id: 'python', label: 'Python' },
    ],
    [],
  );

  return (
    <Layout enableMaxWidth={false}>
      <Layout.MainContainer minWidth={MAIN_CONTAINER_MIN_WIDTH} spacingHorizontal={0} fullWidth>
        <Box display="block" width="100%">
          <VStack>
            <HStack borderedBottom>
              <HStack maxWidth={FULL_PAGE_MAX_WIDTH}>
                <HStack
                  overflow="hidden"
                  maxWidth={FULL_PAGE_MAX_WIDTH}
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  spacingStart={9}
                  spacingEnd={isDesktop ? 0 : 9}
                  spacingBottom={9}
                >
                  <VStack width={isDesktop ? '55%' : '100%'} justifyContent="center">
                    <TextDisplay2 as="h1">{title}</TextDisplay2>
                    <TextTitle4 as="h2" color="foregroundMuted" spacingTop={5} spacingBottom={7}>
                      {description}
                    </TextTitle4>

                    <ButtonGroup>
                      <Button
                        variant="primary"
                        endIcon="diagonalUpArrow"
                        onPress={() => {
                          openUrlInNewWindow(WALLET_API_TRANSFERS_DOCS_LINK);
                        }}
                        accessibilityLabel="Open Send & Receive docs in new window"
                      >
                        Get started
                      </Button>
                      <Button
                        variant="secondary"
                        endIcon="diagonalUpArrow"
                        onPress={() => {
                          openUrlInNewWindow(COINBASE_CDP_SDK_DISCORD_LINK);
                        }}
                        accessibilityLabel="Open CDP SDK Discord channel"
                      >
                        Discord channel
                      </Button>
                    </ButtonGroup>
                  </VStack>
                  {isDesktop && (
                    <Box position="relative" left={100}>
                      <SpotRectangle scaleMultiplier={1.75} name="globalTransactions" />
                    </Box>
                  )}
                </HStack>
              </HStack>
            </HStack>
            {selectedProject && (
              <VStack maxWidth={FULL_PAGE_MAX_WIDTH} spacing={7} gap={3}>
                <TextTitle3 as="h3" spacingStart={4}>
                  Quickstart
                </TextTitle3>
                <VStack spacingStart={4}>
                  <TabNavigation value={selectedTab} tabs={tabs} onChange={setTab} />
                </VStack>
                <VStack spacing={4} gap={6}>
                  <QuickstartItem
                    stepNumber={1}
                    title="Install the SDK"
                    codeBlock={
                      <CodeBlock
                        codeArr={step1Code}
                        showLanguageSelect={false}
                        showCopy
                        customStyle={codeStyle}
                        headerBackground={codeBackgroundColor}
                        enableLineWrap
                        title="shell"
                      />
                    }
                  />
                  <QuickstartItem
                    stepNumber={2}
                    title="Create a wallet on Base Sepolia"
                    codeBlock={
                      <CodeBlock
                        codeArr={step2Code}
                        showLanguageSelect={false}
                        showCopy
                        customStyle={codeStyle}
                        headerBackground={codeBackgroundColor}
                        enableLineWrap
                      />
                    }
                  />
                  <QuickstartItem
                    stepNumber={3}
                    title="Fund your wallet with testnet USDC"
                    codeBlock={
                      <CodeBlock
                        codeArr={step3Code}
                        showLanguageSelect={false}
                        showCopy
                        customStyle={codeStyle}
                        headerBackground={codeBackgroundColor}
                        enableLineWrap
                      />
                    }
                  />
                  <QuickstartItem
                    stepNumber={4}
                    title="Send assets to another wallet"
                    codeBlock={
                      <CodeBlock
                        codeArr={step4Code}
                        showLanguageSelect={false}
                        showCopy
                        customStyle={codeStyle}
                        headerBackground={codeBackgroundColor}
                        enableLineWrap
                      />
                    }
                  />
                </VStack>
              </VStack>
            )}
            <ExampleSection sectionTitle="Related products" exampleItems={learnItems} />
          </VStack>
        </Box>
      </Layout.MainContainer>
    </Layout>
  );
}
